import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FileText, Receipt, ClipboardList, StickyNote,
  Users, Building2, LogOut, Sparkles, Layers, Shield, ScrollText,
  Settings, Activity,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CompanySwitcher from "@/components/CompanySwitcher";
import CompanyScopeFilter from "@/components/CompanyScopeFilter";
import PermissionRequestBell from "@/components/PermissionRequestBell";

const NAV = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
  { to: "/app/invoices", icon: FileText, label: "Invoices", testid: "nav-invoices" },
  { to: "/app/receipts", icon: Receipt, label: "Receipts", testid: "nav-receipts" },
  { to: "/app/purchase-orders", icon: ClipboardList, label: "Purchase Orders", testid: "nav-po" },
  { to: "/app/notes", icon: StickyNote, label: "Credit / Debit Notes", testid: "nav-notes" },
  { to: "/app/templates", icon: Layers, label: "Templates", testid: "nav-templates" },
  { to: "/app/clients", icon: Users, label: "Clients", testid: "nav-clients" },
  { to: "/app/companies", icon: Building2, label: "Companies", testid: "nav-companies" },
];

const ADMIN_NAV = [
  { to: "/app/admin", icon: Shield, label: "Admin Panel", testid: "nav-admin" },
  { to: "/app/admin/audit", icon: ScrollText, label: "Audit Log", testid: "nav-audit" },
  { to: "/app/admin/activity", icon: Activity, label: "Activity Log", testid: "nav-activity" },
  { to: "/app/admin/settings", icon: Settings, label: "Admin Settings", testid: "nav-admin-settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[var(--bg)] flex">
      {/*
        Sidebar is pinned to the viewport height so the user block and logout
        button stay anchored at the bottom. `h-screen` + `sticky` stops the
        panel from stretching to the height of a long page (e.g. the admin
        user table), which used to push the footer off-screen.
      */}
      <aside
        className="w-[248px] shrink-0 border-r border-[var(--border)] bg-white flex flex-col h-screen sticky top-0"
        data-testid="app-sidebar"
      >
        <div className="px-5 py-4 border-b border-[var(--border)] flex items-center gap-2 shrink-0">
          <div className="w-8 h-8 bg-[var(--fg)] text-white grid place-items-center rounded-md shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold tracking-tight leading-none truncate">InvoiceForge</div>
            <div className="overline mt-1">Billing OS</div>
          </div>
        </div>

        {/*
          min-h-0 is required: a flex child defaults to min-height:auto and
          would refuse to shrink below its content, re-introducing the
          scrolling-sidebar bug on tall pages.
        */}
        <div className="p-3 flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-0.5">
          <div className="overline px-3 pt-1 pb-2 shrink-0">Workspace</div>
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) => `sidebar-item shrink-0 ${isActive ? "active" : ""}`}
              >
                <Icon className="w-4 h-4 icon shrink-0" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
          {user?.role === "admin" && (
            <>
              <div className="overline px-3 py-2 mt-3 shrink-0">Administration</div>
              {ADMIN_NAV.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testid}
                    className={({ isActive }) => `sidebar-item shrink-0 ${isActive ? "active" : ""}`}
                  >
                    <Icon className="w-4 h-4 icon shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                );
              })}
            </>
          )}
          <div className="overline px-3 py-2 mt-3 shrink-0">Account</div>
          <NavLink
            to="/app/settings"
            data-testid="nav-settings"
            className={({ isActive }) => `sidebar-item shrink-0 ${isActive ? "active" : ""}`}
          >
            <Settings className="w-4 h-4 icon shrink-0" />
            <span className="truncate">Settings</span>
          </NavLink>
        </div>

        <div className="p-3 border-t border-[var(--border)] shrink-0 bg-white">
          <div className="flex items-center gap-2.5 px-1.5 py-1.5">
            <div className="w-8 h-8 shrink-0 rounded-full bg-[var(--muted)] grid place-items-center text-xs font-semibold">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate leading-tight" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[11px] text-gray-500 truncate">{user?.email}</div>
            </div>
            <button
              className="p-2 shrink-0 hover:bg-gray-100 rounded"
              data-testid="btn-logout"
              onClick={logout}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/*
          Two company controls with different jobs, side by side:

          CompanySwitcher sets the *active* company, which decides whose
          letterhead, bank details and numbering a new document uses. It is
          persisted server-side.

          CompanyScopeFilter sets the *view* scope: which companies the lists
          and dashboard totals cover. It is local to this browser and supports
          one company, several, or all of them.

          Keeping them adjacent makes the difference legible instead of leaving
          the user to guess why their filter "did not apply" to the company they
          just switched to.
        */}
        <header className="h-14 shrink-0 border-b border-[var(--border)] bg-white/80 backdrop-blur sticky top-0 z-30 flex items-center justify-end gap-2 px-5">
          <CompanyScopeFilter />
          <CompanySwitcher />
          <PermissionRequestBell />
        </header>
        <main className="flex-1 min-w-0">
          <Outlet key={location.pathname} />
        </main>
      </div>
    </div>
  );
}
